import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  contentHash,
  missingSalesFields,
  normalizeOriginalPayload,
  previewOriginalPayload,
  countOriginalStops,
  type OriginalPreview,
  type OriginalRoutePayload,
} from "@/lib/originals";
import {
  type StripeEnv,
  createStripeClient,
  getServerStripeEnvironment,
  getStripeErrorMessage,
} from "@/lib/stripe.server";

export type OriginalRouteCard = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  destination: string;
  countryCode: string | null;
  heroImageUrl: string | null;
  days: number;
  stopsCount: number;
  priceCents: number;
  currency: string;
  publishedVersion: number | null;
};

function publicClient() {
  return createClient(process.env["SUPABASE_URL"]!, process.env["SUPABASE_PUBLISHABLE_KEY"]!, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
}

function toCard(row: any): OriginalRouteCard {
  return {
    id: row.id,
    slug: row.slug,
    title: row.title,
    subtitle: row.subtitle ?? null,
    destination: row.destination,
    countryCode: row.country_code ?? null,
    heroImageUrl: row.hero_image_url ?? null,
    days: row.days ?? 1,
    stopsCount: row.stops_count ?? 0,
    priceCents: row.price_cents ?? 0,
    currency: row.currency ?? "eur",
    publishedVersion: row.published_version ?? null,
  };
}

const CARD_COLUMNS =
  "id,slug,title,subtitle,destination,country_code,hero_image_url,days,stops_count,price_cents,currency,published_version";

/** Loja pública: só rotas publicadas, sem conteúdo completo. */
export const listPublishedOriginals = createServerFn({ method: "GET" }).handler(
  async (): Promise<OriginalRouteCard[]> => {
    const { data, error } = await publicClient()
      .from("original_routes")
      .select(CARD_COLUMNS)
      .eq("status", "published")
      .order("sort_order", { ascending: true })
      .order("created_at", { ascending: false })
      .limit(60);
    if (error) throw new Error(error.message);
    return (data ?? []).map(toCard);
  },
);

/** Ficha pública de uma Original: metadados + pré-visualização honesta. */
export const getOriginalPreview = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => {
    const slug = (data as { slug?: unknown })?.slug;
    if (typeof slug !== "string" || !/^[a-z0-9-]{2,80}$/.test(slug)) {
      throw new Error("Rota inválida");
    }
    return { slug };
  })
  .handler(
    async ({
      data,
    }): Promise<{ route: OriginalRouteCard; preview: OriginalPreview } | null> => {
      const client = publicClient();
      const { data: row, error } = await client
        .from("original_routes")
        .select(CARD_COLUMNS)
        .eq("slug", data.slug)
        .eq("status", "published")
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!row) return null;
      const card = toCard(row);

      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: version } = await supabaseAdmin
        .from("original_route_versions")
        .select("payload")
        .eq("route_id", card.id)
        .eq("version", card.publishedVersion ?? 1)
        .maybeSingle();
      const payload = normalizeOriginalPayload(version?.payload ?? { days: [] });
      return { route: card, preview: previewOriginalPayload(payload) };
    },
  );

export type PurchasedOriginal = {
  route: OriginalRouteCard;
  purchaseId: string;
  version: number;
  purchasedAt: string;
  hidden: boolean;
  payload: OriginalRoutePayload;
};

/** Conteúdo completo — só para quem comprou. */
export const getPurchasedOriginal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const slug = (data as { slug?: unknown })?.slug;
    if (typeof slug !== "string" || !/^[a-z0-9-]{2,80}$/.test(slug)) {
      throw new Error("Rota inválida");
    }
    return { slug };
  })
  .handler(async ({ data, context }): Promise<PurchasedOriginal | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("original_routes")
      .select(CARD_COLUMNS)
      .eq("slug", data.slug)
      .maybeSingle();
    if (!row) return null;
    const card = toCard(row);

    const { data: purchase } = await supabaseAdmin
      .from("original_route_purchases")
      .select("id,version,created_at,hidden_at")
      .eq("user_id", context.userId)
      .eq("route_id", card.id)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!purchase) return null;

    // Quem comprou tem sempre acesso à versão mais recente publicada.
    const version = Math.max(
      Number(purchase.version ?? 1),
      Number(card.publishedVersion ?? purchase.version ?? 1),
    );
    const { data: versionRow } = await supabaseAdmin
      .from("original_route_versions")
      .select("payload")
      .eq("route_id", card.id)
      .eq("version", version)
      .maybeSingle();

    return {
      route: card,
      purchaseId: purchase.id as string,
      version,
      purchasedAt: purchase.created_at,
      hidden: Boolean(purchase.hidden_at),
      payload: normalizeOriginalPayload(versionRow?.payload ?? { days: [] }),
    };
  });

export type MyOriginalPurchase = OriginalRouteCard & {
  purchaseId: string;
  version: number;
  purchasedAt: string;
  hidden: boolean;
};

export const listMyOriginalPurchases = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<MyOriginalPurchase[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: purchases } = await supabaseAdmin
      .from("original_route_purchases")
      .select("id,route_id,version,created_at,hidden_at")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(200);
    if (!purchases?.length) return [];
    const { data: routes } = await supabaseAdmin
      .from("original_routes")
      .select(CARD_COLUMNS)
      .in("id", purchases.map((p: any) => p.route_id));
    const byId = new Map((routes ?? []).map((r: any) => [r.id, toCard(r)]));
    return purchases
      .map((p: any) => {
        const card = byId.get(p.route_id);
        if (!card) return null;
        return {
          ...card,
          purchaseId: p.id,
          version: p.version ?? 1,
          purchasedAt: p.created_at,
          hidden: Boolean(p.hidden_at),
        } satisfies MyOriginalPurchase;
      })
      .filter((v): v is MyOriginalPurchase => v != null);
  });

/** Remover da lista NÃO cancela a compra: esconde e pode ser reposta. */
export const setOriginalPurchaseHidden = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const input = (data ?? {}) as { purchaseId?: unknown; hidden?: unknown };
    if (typeof input.purchaseId !== "string" || !input.purchaseId) {
      throw new Error("Compra inválida");
    }
    return { purchaseId: input.purchaseId, hidden: Boolean(input.hidden) };
  })
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("original_route_purchases")
      .update({ hidden_at: data.hidden ? new Date().toISOString() : null })
      .eq("id", data.purchaseId)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message || "Não foi possível atualizar a rota");
    return { ok: true };
  });

/**
 * Resumo leve para o cartão em "As minhas rotas": só contadores, sem
 * previews nem payloads. Zero chamadas a fornecedores externos.
 */
export const getOriginalsSummary = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase } = context;

    const published = await supabase
      .from("original_routes")
      .select("id", { count: "exact", head: true })
      .eq("status", "published");

    const purchased = await supabase
      .from("original_route_purchases")
      .select("id", { count: "exact", head: true })
      .eq("user_id", context.userId);

    const visible = await supabase
      .from("original_route_purchases")
      .select("id", { count: "exact", head: true })
      .eq("user_id", context.userId)
      .is("hidden_at", null);

    return {
      publishedOriginalCount: published.count ?? 0,
      purchasedOriginalCount: purchased.count ?? 0,
      visibleOriginalCount: visible.count ?? 0,
    };
  });

function isAllowedReturnUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname;
    return (
      host.endsWith(".lovable.app") ||
      host.endsWith(".lovable.dev") ||
      host.endsWith(".lovableproject.com") ||
      host === "localhost" ||
      host === "127.0.0.1" ||
      host === "routify-tour.com" ||
      host.endsWith(".routify-tour.com")
    );
  } catch {
    return false;
  }
}

/**
 * Checkout de uma Original. O preço vem SEMPRE da base de dados, nunca do
 * browser, e a compra fica ligada à conta pelos metadados.
 */
export const createOriginalCheckoutSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const input = (data ?? {}) as {
      slug?: unknown;
      returnUrl?: unknown;
      environment?: unknown;
      source?: unknown;
    };
    if (typeof input.slug !== "string" || !/^[a-z0-9-]{2,80}$/.test(input.slug)) {
      throw new Error("Rota inválida");
    }
    if (typeof input.returnUrl !== "string" || !isAllowedReturnUrl(input.returnUrl)) {
      throw new Error("Invalid returnUrl");
    }
    return {
      slug: input.slug,
      returnUrl: input.returnUrl,
      environment: input.environment as StripeEnv,
      source: typeof input.source === "string" ? input.source.trim().toLowerCase().slice(0, 40) : "",
    };
  })
  .handler(async ({ data, context }): Promise<{ url?: string } | { error: string }> => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: rowData } = await supabaseAdmin
        .from("original_routes")
        .select(CARD_COLUMNS + ",status")
        .eq("slug", data.slug)
        .maybeSingle();
      const row = rowData as any;
      if (!row || row.status !== "published") return { error: "Esta rota não está disponível." };
      const card = toCard(row);
      if (card.priceCents < 50) return { error: "Esta rota ainda não tem preço definido." };

      const environment = getServerStripeEnvironment(data.environment);

      const { data: existing } = await supabaseAdmin
        .from("original_route_purchases")
        .select("id")
        .eq("user_id", context.userId)
        .eq("route_id", card.id)
        .limit(1)
        .maybeSingle();
      if (existing) return { error: "Já tens esta rota na tua conta." };

      const stripe = createStripeClient(environment);
      const { data: userData } = await context.supabase.auth.getUser();

      const session = await stripe.checkout.sessions.create({
        mode: "payment",
        locale: "pt",
        line_items: [
          {
            price_data: {
              currency: card.currency,
              unit_amount: card.priceCents,
              product_data: {
                name: `Routify Original — ${card.title}`,
                ...(card.subtitle ? { description: card.subtitle } : {}),
              },
            },
            quantity: 1,
          },
        ],
        automatic_tax: { enabled: true },
        ...(userData.user?.email ? { customer_email: userData.user.email } : {}),
        payment_intent_data: { description: `Routify Original — ${card.title}` },
        metadata: {
          userId: context.userId,
          original_route_id: card.id,
          original_route_slug: card.slug,
          original_route_version: String(card.publishedVersion ?? 1),
          ...(data.source && /^[a-z0-9_.-]{1,40}$/.test(data.source)
            ? { original_source: data.source }
            : {}),
        },
        success_url: data.returnUrl,
        cancel_url: data.returnUrl,
      } as any);

      return { url: session.url ?? "" };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

/* ------------------------------------------------------------------ */
/* Visitas (contagem honesta, sem dados pessoais)                        */
/* ------------------------------------------------------------------ */

/** Origens aceites; qualquer outra coisa passa a "unknown". */
function normalizeSource(value: unknown): string {
  const raw = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (!raw || !/^[a-z0-9_.-]{1,40}$/.test(raw)) return "unknown";
  return raw;
}

/**
 * Endpoint público de estatísticas. O browser NUNCA escreve na base de dados:
 * este servidor decide o ambiente (live/test), valida o slug, normaliza a
 * origem e só depois chama a função interna (executável só pelo servidor).
 * Falhas são silenciosas — contar nunca pode impedir ver ou comprar.
 */
export const recordOriginalView = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => {
    const input = (data ?? {}) as { slug?: unknown; kind?: unknown; source?: unknown };
    if (typeof input.slug !== "string" || !/^[a-z0-9-]{1,80}$/.test(input.slug)) {
      throw new Error("Rota inválida");
    }
    return {
      slug: input.slug,
      kind: input.kind === "buy_click" ? "buy_click" : "view",
      source: normalizeSource(input.source),
    };
  })
  .handler(async ({ data }) => {
    try {
      const { describeServerDisplayPaymentEnvironment } = await import("@/lib/stripe.server");
      const environment =
        describeServerDisplayPaymentEnvironment().environment === "live" ? "live" : "test";
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await supabaseAdmin.rpc("record_original_view", {
        _slug: data.slug,
        _environment: environment,
        _kind: data.kind,
        _source: data.source,
      } as never);
    } catch {
      // Contagem nunca pode quebrar a página pública.
    }
    return { ok: true };
  });

/**
 * Cartão público "Rotas prontas da equipa": quantas Originais estão à venda e
 * o menor preço actual. Sem sessão iniciada e sem chamadas a fornecedores.
 */
export const getOriginalsStorefrontTeaser = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ count: number; minPriceCents: number; currency: string } | null> => {
    const { data, error } = await publicClient()
      .from("original_routes")
      .select("price_cents,currency")
      .eq("status", "published")
      .gte("price_cents", 50)
      .order("price_cents", { ascending: true })
      .limit(60);
    if (error || !data?.length) return null;
    return {
      count: data.length,
      minPriceCents: Number((data[0] as any).price_cents) || 0,
      currency: ((data[0] as any).currency as string) || "eur",
    };
  },
);

/* ------------------------------------------------------------------ */
/* Painel do Fundador                                                   */
/* ------------------------------------------------------------------ */

async function assertAdmin(userId: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin.rpc("is_admin", { _user_id: userId });
  if (data !== true) throw new Error("Sem permissão");
  return supabaseAdmin;
}

export const listAllOriginals = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const supabaseAdmin = await assertAdmin(context.userId);
    const { data } = await supabaseAdmin
      .from("original_routes")
      .select(CARD_COLUMNS + ",status,updated_at")
      .order("updated_at", { ascending: false })
      .limit(100);
    return (data ?? []).map((row: any) => ({
      ...toCard(row),
      status: row.status as "draft" | "published" | "archived",
      updatedAt: row.updated_at as string,
    }));
  });

/**
 * Cria/atualiza uma Original e guarda uma nova versão do conteúdo editorial.
 * O payload é sempre normalizado — dados do fornecedor nunca são guardados.
 */
export const saveOriginalRoute = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const input = (data ?? {}) as Record<string, unknown>;
    const slug = typeof input.slug === "string" ? input.slug.trim().toLowerCase() : "";
    if (!/^[a-z0-9-]{2,80}$/.test(slug)) throw new Error("Endereço inválido (usa letras, números e hífenes)");
    const title = typeof input.title === "string" ? input.title.trim() : "";
    if (!title) throw new Error("Falta o título");
    const destination = typeof input.destination === "string" ? input.destination.trim() : "";
    if (!destination) throw new Error("Falta o destino");
    return {
      id: typeof input.id === "string" ? input.id : undefined,
      slug,
      title,
      destination,
      subtitle: typeof input.subtitle === "string" ? input.subtitle.trim() : "",
      countryCode: typeof input.countryCode === "string" ? input.countryCode.trim() : "",
      heroImageUrl: typeof input.heroImageUrl === "string" ? input.heroImageUrl.trim() : "",
      priceCents: Number(input.priceCents) || 0,
      currency: typeof input.currency === "string" && input.currency ? input.currency : "eur",
      payload: input.payload,
      publish: Boolean(input.publish),
    };
  })
  .handler(async ({ data, context }) => {
    const supabaseAdmin = await assertAdmin(context.userId);
    const payload = normalizeOriginalPayload(data.payload ?? { days: [] });
    const stops = countOriginalStops(payload);

    if (data.publish) {
      const missing = missingSalesFields(payload, {
        heroImageUrl: data.heroImageUrl,
        priceCents: data.priceCents,
      });
      if (missing.length) {
        throw new Error(`Falta preencher para publicar: ${missing.join(", ")}`);
      }
    }

    const base = {
      slug: data.slug,
      title: data.title,
      subtitle: data.subtitle || null,
      destination: data.destination,
      country_code: data.countryCode || null,
      hero_image_url: data.heroImageUrl || null,
      days: Math.max(1, payload.days.length),
      stops_count: stops,
      price_cents: Math.max(0, Math.round(data.priceCents)),
      currency: data.currency,
      created_by: context.userId,
    };

    let routeId = data.id;

    // O endereço (slug) é único: avisa em linguagem clara em vez de deixar
    // passar o erro cru da base de dados ("duplicate key ... slug_key").
    const { data: sameSlug } = await supabaseAdmin
      .from("original_routes")
      .select("id,title")
      .eq("slug", data.slug)
      .maybeSingle();
    if (sameSlug && sameSlug.id !== routeId) {
      throw new Error(
        `Já existe uma rota com o endereço "${data.slug}" (${sameSlug.title}). Escolhe outro endereço ou abre essa rota para a editar.`,
      );
    }

    if (routeId) {
      const { error } = await supabaseAdmin.from("original_routes").update(base).eq("id", routeId);
      if (error) throw new Error(error.message);
    } else {
      const { data: inserted, error } = await supabaseAdmin
        .from("original_routes")
        .insert(base)
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      routeId = inserted.id as string;
    }

    const { data: last } = await supabaseAdmin
      .from("original_route_versions")
      .select("version")
      .eq("route_id", routeId)
      .order("version", { ascending: false })
      .limit(1)
      .maybeSingle();
    const nextVersion = (last?.version ?? 0) + 1;

    const { error: versionError } = await supabaseAdmin.from("original_route_versions").insert({
      route_id: routeId,
      version: nextVersion,
      payload,
      content_hash: contentHash(payload),
      created_by: context.userId,
      ...(data.publish ? { published_at: new Date().toISOString() } : {}),
    });
    if (versionError) throw new Error(versionError.message);

    if (data.publish) {
      const { error } = await supabaseAdmin
        .from("original_routes")
        .update({ status: "published", published_version: nextVersion })
        .eq("id", routeId);
      if (error) throw new Error(error.message);
    }

    return { ok: true, id: routeId, version: nextVersion, stops };
  });

export const setOriginalStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const input = (data ?? {}) as { id?: unknown; status?: unknown };
    if (typeof input.id !== "string" || !input.id) throw new Error("Rota inválida");
    if (input.status !== "draft" && input.status !== "published" && input.status !== "archived") {
      throw new Error("Estado inválido");
    }
    return { id: input.id, status: input.status };
  })
  .handler(async ({ data, context }) => {
    const supabaseAdmin = await assertAdmin(context.userId);
    if (data.status === "published") {
      const { data: row } = await supabaseAdmin
        .from("original_routes")
        .select(CARD_COLUMNS)
        .eq("id", data.id)
        .maybeSingle();
      if (!row) throw new Error("Rota inválida");
      const card = toCard(row);
      const { data: versionRow } = await supabaseAdmin
        .from("original_route_versions")
        .select("payload")
        .eq("route_id", data.id)
        .order("version", { ascending: false })
        .limit(1)
        .maybeSingle();
      const missing = missingSalesFields(
        normalizeOriginalPayload(versionRow?.payload ?? { days: [] }),
        card,
      );
      if (missing.length) {
        throw new Error(`Falta preencher para publicar: ${missing.join(", ")}`);
      }
    }
    const { error } = await supabaseAdmin
      .from("original_routes")
      .update({ status: data.status })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

/* ------------------------------------------------------------------ */
/* Página de vendas (só admin)                                          */
/* ------------------------------------------------------------------ */

export type SalesMoneyRow = {
  grossCents: number;
  taxCents: number;
  refundsCents: number;
  feesCents: number;
  netCents: number;
  sales: number;
};

export type SourceRow = { source: string; views: number; buyClicks: number; sales: number };

export type RouteSalesRow = {
  routeId: string | null;
  slug: string;
  title: string;
  views: number;
  buyClicks: number;
  sales: number;
  conversionPct: number;
  netCents: number;
  sources: SourceRow[];
};

export type RecentPurchaseRow = {
  date: string;
  kind: "original" | "credits" | "subscription" | "other";
  label: string;
  amountCents: number;
  currency: string;
  status: "paid" | "refunded" | "failed";
  email: string | null;
  environment: string;
};

export type OriginalSalesReport = {
  environment: "live" | "test";
  totals: Record<"originals" | "credits" | "subscriptions", SalesMoneyRow>;
  routes: RouteSalesRow[];
  storeViews: number;
  purchases: RecentPurchaseRow[];
};

const ZERO_MONEY: SalesMoneyRow = {
  grossCents: 0,
  taxCents: 0,
  refundsCents: 0,
  feesCents: 0,
  netCents: 0,
  sales: 0,
};

/**
 * Relatório de vendas com o Stripe como fonte da verdade para o dinheiro:
 * sessões de checkout (bruto + IVA + origem) cruzadas com transações de
 * saldo (taxas e reembolsos). Reembolsos e falhas nunca contam como receita.
 * O e-mail do comprador só sai desta função, autenticada e admin.
 */
export const getOriginalSalesReport = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const input = (data ?? {}) as { environment?: unknown; days?: unknown };
    const days = Math.min(366, Math.max(7, Number(input.days) || 30));
    return {
      environment: input.environment === "test" ? "test" : "live",
      days,
    };
  })
  .handler(async ({ data, context }): Promise<OriginalSalesReport> => {
    // Papel validado como utilizador autenticado, nunca com cliente admin.
    const { data: isAdmin } = await context.supabase.rpc("is_admin", {
      _user_id: context.userId,
    });
    if (isAdmin !== true) throw new Error("Sem permissão");

    const stripeEnv = data.environment === "test" ? "sandbox" : "live";
    const environmentLabel: "live" | "test" = data.environment === "test" ? "test" : "live";
    const stripe = createStripeClient(stripeEnv);
    const since = Math.floor(Date.now() / 1000) - data.days * 86400;

    // --- Stripe: sessões + taxas/reembolsos ---
    const totals: OriginalSalesReport["totals"] = {
      originals: { ...ZERO_MONEY },
      credits: { ...ZERO_MONEY },
      subscriptions: { ...ZERO_MONEY },
    };
    const purchases: RecentPurchaseRow[] = [];
    const sessionIds: string[] = [];

    for await (const session of stripe.checkout.sessions.list({
      created: { gte: since },
      expand: ["data.payment_intent"],
      limit: 100,
    }) as any) {
      const meta = (session.metadata ?? {}) as Record<string, string>;
      const amount = Number(session.amount_total ?? 0);
      const tax = Number(session.total_details?.amount_tax ?? 0);
      const isOriginal = Boolean(meta.original_route_id || meta.original_route_slug);
      const isCredits = Boolean(meta.credits_pack_lookup_key);
      const kind: SalesMoneyRow["grossCents"] extends number
        ? "originals" | "credits" | "subscriptions"
        : never = isOriginal ? "originals" : isCredits ? "credits" : "subscriptions";
      const status: RecentPurchaseRow["status"] =
        session.payment_status === "paid" ? "paid" : "failed";
      purchases.push({
        date: new Date(session.created * 1000).toISOString(),
        kind: isOriginal ? "original" : isCredits ? "credits" : session.mode === "subscription" ? "subscription" : "other",
        label:
          meta.original_route_slug
            ? `Original — ${meta.original_route_slug}`
            : (meta.credits_pack_lookup_key ?? (session.mode === "subscription" ? "Subscrição" : "Pagamento")),
        amountCents: amount,
        currency: session.currency ?? "eur",
        status,
        email: session.customer_details?.email ?? null,
        environment: environmentLabel,
      });
      if (status !== "paid") continue; // falhas nunca contam como receita
      totals[kind].grossCents += amount;
      totals[kind].taxCents += tax;
      totals[kind].sales += 1;
      const pi = session.payment_intent as { id?: string } | null;
      if (session.id) sessionIds.push(session.id);
      if (pi?.id) sessionIds.push(pi.id);
    }

    // --- Stripe: taxas e reembolsos (transações de saldo) ---
    // Associar taxas/reembolsos a cada sessão custaria uma chamada extra por
    // charge; agregamos a nível global por ambiente (preciso) e dividimos por
    // origem proporcionalmente ao bruto (estimativa honesta).
    let totalFees = 0;
    let totalRefunds = 0;
    for await (const bt of stripe.balanceTransactions.list({ created: { gte: since }, limit: 100 }) as any) {
      if (bt.type === "charge") totalFees += Math.abs(Number(bt.fee ?? 0));
      if (bt.type === "refund") totalRefunds += Math.abs(Number(bt.amount ?? 0));
    }
    const grossTotal = totals.originals.grossCents + totals.credits.grossCents + totals.subscriptions.grossCents;
    for (const key of ["originals", "credits", "subscriptions"] as const) {
      const row = totals[key];
      const share = grossTotal > 0 ? row.grossCents / grossTotal : 0;
      row.feesCents = Math.round(totalFees * share);
      row.refundsCents = Math.round(totalRefunds * share);
      row.netCents = row.grossCents - row.refundsCents - row.feesCents;
    }
    void sessionIds;

    // --- Base de dados: compras por rota + visitas ---
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const envCol = stripeEnv === "sandbox" ? "test" : "live";
    const sinceIso = new Date(since * 1000).toISOString();

    const { data: dbPurchases } = await supabaseAdmin
      .from("original_route_purchases")
      .select("route_id,amount_cents,created_at,source")
      .eq("environment", envCol)
      .gte("created_at", sinceIso)
      .limit(2000);

    const { data: routesRows } = await supabaseAdmin
      .from("original_routes")
      .select("id,slug,title")
      .limit(200);
    const routeMeta = new Map(
      (routesRows ?? []).map((r: any) => [r.id as string, { slug: r.slug, title: r.title }]),
    );

    const { data: viewsRows } = await supabaseAdmin
      .from("original_route_daily_metrics")
      .select("route_id,scope,views,buy_clicks,source")
      .eq("environment", envCol)
      .limit(5000);

    let storeViews = 0;
    const viewsByRoute = new Map<string, number>();
    const clicksByRoute = new Map<string, number>();
    /** routeId -> source -> contadores */
    const sourceByRoute = new Map<string, Map<string, SourceRow>>();
    const bumpSource = (routeId: string, source: string, patch: Partial<SourceRow>) => {
      const bucket = sourceByRoute.get(routeId) ?? new Map<string, SourceRow>();
      const key = source || "unknown";
      const row = bucket.get(key) ?? { source: key, views: 0, buyClicks: 0, sales: 0 };
      row.views += patch.views ?? 0;
      row.buyClicks += patch.buyClicks ?? 0;
      row.sales += patch.sales ?? 0;
      bucket.set(key, row);
      sourceByRoute.set(routeId, bucket);
    };

    for (const row of viewsRows ?? []) {
      const views = Number(row.views ?? 0);
      const clicks = Number((row as any).buy_clicks ?? 0);
      if (row.scope === "store") storeViews += views;
      else if (row.route_id) {
        viewsByRoute.set(row.route_id, (viewsByRoute.get(row.route_id) ?? 0) + views);
        clicksByRoute.set(row.route_id, (clicksByRoute.get(row.route_id) ?? 0) + clicks);
        bumpSource(row.route_id, String((row as any).source ?? "unknown"), {
          views,
          buyClicks: clicks,
        });
      }
    }

    const salesByRoute = new Map<string, { sales: number; netCents: number }>();
    for (const p of dbPurchases ?? []) {
      const routeId = p.route_id as string;
      const row = salesByRoute.get(routeId) ?? { sales: 0, netCents: 0 };
      row.sales += 1;
      row.netCents += Number(p.amount_cents ?? 0);
      salesByRoute.set(routeId, row);
      bumpSource(routeId, String((p as any).source ?? "unknown"), { sales: 1 });
    }

    const sourcesFor = (routeId: string): SourceRow[] =>
      [...(sourceByRoute.get(routeId)?.values() ?? [])].sort((a, b) => b.views - a.views);

    const routes: RouteSalesRow[] = [...salesByRoute.entries()]
      .map(([routeId, agg]) => {
        const meta = routeMeta.get(routeId);
        const views = viewsByRoute.get(routeId) ?? 0;
        return {
          routeId,
          slug: meta?.slug ?? routeId,
          title: meta?.title ?? routeId,
          views,
          buyClicks: clicksByRoute.get(routeId) ?? 0,
          sales: agg.sales,
          conversionPct: views > 0 ? Math.round((agg.sales / views) * 1000) / 10 : 0,
          netCents: agg.netCents,
          sources: sourcesFor(routeId),
        };
      })
      .sort((a, b) => b.netCents - a.netCents);

    // Rotas com visitas mas sem compras também aparecem.
    for (const [routeId, views] of viewsByRoute) {
      if (salesByRoute.has(routeId)) continue;
      const meta = routeMeta.get(routeId);
      if (!meta) continue;
      routes.push({
        routeId,
        slug: meta.slug,
        title: meta.title,
        views,
        buyClicks: clicksByRoute.get(routeId) ?? 0,
        sales: 0,
        conversionPct: 0,
        netCents: 0,
        sources: sourcesFor(routeId),
      });
    }

    purchases.sort((a, b) => (a.date < b.date ? 1 : -1));


    return {
      environment: environmentLabel,
      totals,
      routes,
      storeViews,
      purchases: purchases.slice(0, 100),
    };
  });

/** Conteúdo editorial da versão mais recente (painel). */
export const getOriginalDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const id = (data as { id?: unknown })?.id;
    if (typeof id !== "string" || !id) throw new Error("Rota inválida");
    return { id };
  })
  .handler(async ({ data, context }): Promise<OriginalRoutePayload> => {
    const supabaseAdmin = await assertAdmin(context.userId);
    const { data: row } = await supabaseAdmin
      .from("original_route_versions")
      .select("payload")
      .eq("route_id", data.id)
      .order("version", { ascending: false })
      .limit(1)
      .maybeSingle();
    return normalizeOriginalPayload(row?.payload ?? { days: [] });
  });
